import { defineStore } from 'pinia';

import type { Note } from '@stuut/api/models';
import type { CreateNoteData, UpdateNoteData } from '@stuut/api/resources';

import { stuut } from '@/api';

interface State {
  filters: Record<string, any>;
  notes: Note[];
  numItemsPerPage: number;
  numTotalItems: number;
  page: number;
}

export const useNotes = defineStore('notes', {
  actions: {
    async createNote(createNoteData: CreateNoteData) {
      const note = await stuut.notes.create(createNoteData);

      this.fetchNotesByPage(this.page);

      return note;
    },

    async deleteNote(note: Note) {
      await stuut.notes.delete(note.id);

      // Technically we should be calculating the last page from total
      // number of items, but this is more readable.
      await this.fetchNotesByPage(this.page);
      if (this.notes.length === 0 && this.page > 0) {
        await this.fetchNotesByPage(this.page - 1);
      }
    },

    async fetchNotesByPage(page: number) {
      const { data, pageInfo } = await stuut.notes.listWithPageInfo({
        params: {
          limit: this.numItemsPerPage,
          page,
          ...this.filters,
        },
      });

      this.page = page;
      this.notes = data;
      this.numTotalItems = pageInfo.total_records ?? 0;

      return { data, pageInfo };
    },

    async init(numItemsPerPage: number, filters: Record<string, any>) {
      this.numItemsPerPage = numItemsPerPage;
      this.filters = filters;
    },

    async updateNote(note: Note, updateNoteData: UpdateNoteData) {
      const author = note.author;

      const updatedNote = await stuut.notes.update(note.id, {
        data: {
          ...updateNoteData,
        },
      });

      if (!updatedNote.author) updatedNote.author = author;

      // Don't fetch notes by page here, because we should ensure
      // users see the result of pinning or updating the note immediately.
      this.notes = this.notes.map((n) => (n.id === note.id ? updatedNote : n));

      return updatedNote;
    },
  },
  state: (): State => ({
    filters: {},
    notes: [],
    numItemsPerPage: 0,
    numTotalItems: 0,
    page: 0,
  }),
});

export default useNotes;
